import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {handleResponse, jsonInit} from './client';
import {useAuth} from '../hooks/useAuth';
import {PreparedPhoto} from '../lib/resizeImage';
import {Place, PlaceDetail, PlaceInput} from '../types';

export const placesKey = (includeHidden: boolean) => ['places', includeHidden] as const;

export function usePlaces(includeHidden = false) {
    const {call} = useAuth();
    return useQuery({
        queryKey: placesKey(includeHidden),
        queryFn: () => call<{items: Place[]}>(`/api/places${includeHidden ? '?includeHidden=1' : ''}`).then(r => r.items),
        staleTime: 5 * 60 * 1000
    });
}

/** The place page: the place, its area, its beds, and what is growing there now. */
export function usePlace(id: string | undefined) {
    const {call} = useAuth();
    return useQuery({queryKey: ['places', 'detail', id], queryFn: () => call<PlaceDetail>(`/api/places/${id}`), enabled: !!id, staleTime: 30 * 1000});
}

/** Create, patch, reorder and delete, each invalidating both place lists. */
export function usePlaceMutations() {
    const {call} = useAuth();
    const queryClient = useQueryClient();
    const refresh = () => queryClient.invalidateQueries({queryKey: ['places']});

    const create = useMutation({
        mutationFn: (input: PlaceInput) => call<Place>('/api/places', jsonInit('POST', input)),
        onSuccess: refresh,
        meta: {silent: true}
    });

    const patch = useMutation({
        mutationFn: ({id, ...input}: PlaceInput & {id: string}) => call<Place>(`/api/places/${id}`, jsonInit('PATCH', input)),
        onSuccess: refresh,
        meta: {silent: true}
    });

    const reorder = useMutation({
        mutationFn: (input: {parentId: string | null; ids: string[]}) => call<void>('/api/places/order', jsonInit('PUT', input)),
        // Optimistic: the list is local and the server only renumbers.
        onMutate: async ({parentId, ids}) => {
            await queryClient.cancelQueries({queryKey: ['places']});
            const apply = (items: Place[] | undefined) =>
                items?.map(p => (p.parentId === parentId && ids.includes(p.id) ? {...p, sortOrder: (ids.indexOf(p.id) + 1) * 100} : p));
            queryClient.setQueryData<Place[]>(placesKey(false), apply);
            queryClient.setQueryData<Place[]>(placesKey(true), apply);
        },
        onSettled: refresh
    });

    const remove = useMutation({
        mutationFn: (id: string) => call<void>(`/api/places/${id}`, {method: 'DELETE'}),
        onSuccess: refresh
    });

    return {create, patch, reorder, remove};
}

/** A place's one photo: upload (replaces) and remove. The resize happens before calling. */
export function usePlacePhoto() {
    const {authedFetch, call} = useAuth();
    const queryClient = useQueryClient();
    const refresh = () => queryClient.invalidateQueries({queryKey: ['places']});

    const upload = useMutation({
        mutationFn: async ({id, prepared}: {id: string; prepared: PreparedPhoto}) => {
            const form = new FormData();
            form.append('file', prepared.main, 'photo.jpg');
            if (prepared.thumb) form.append('thumbnail', prepared.thumb, 'thumb.jpg');
            form.append('width', String(prepared.width));
            form.append('height', String(prepared.height));
            form.append('crc32', String(prepared.crc32));
            return handleResponse<{photoUrl: string; photoThumbUrl: string | null}>(await authedFetch(`/api/places/${id}/photo`, {method: 'PUT', body: form}));
        },
        onSuccess: refresh,
        meta: {silent: true}
    });

    const remove = useMutation({
        mutationFn: (id: string) => call<void>(`/api/places/${id}/photo`, {method: 'DELETE'}),
        onSuccess: refresh
    });

    return {upload, remove};
}
